<template>
  <div class="t-t-font-collapsable">
    <div class="mb-2 flex items-center justify-between">
      <div
        v-if="$t('forms.font_settings')"
        class="font-inter text-[13px] font-medium leading-6 text-gray-100"
      >
        {{ $t("forms.font_settings") }}
      </div>
      <div
        class="ml-auto cursor-pointer font-inter text-xs font-medium leading-6 text-accent-1"
        data-testid="toggle-collapse"
        @click="collapsed = !collapsed"
      >
        {{
          collapsed
            ? $t("forms.font_size_color_less")
            : $t("forms.font_size_color_more")
        }}
      </div>
    </div>
    <FontPicker
      :color="color"
      :selected-size="size"
      :selected-font="fontFamily"
      :search-font="$t('forms.search_font')"
      :colors="colors"
      :template-colors="templateColors"
      :template-colors-label="$t('color_picker.template_colors')"
      :predefined-colors-label="$t('color_picker.predefined_colors')"
      @get-template-colors="getAllColorsFromTemplate"
      @color-changed="color = $event"
      @size-changed="size = $event"
      @font-changed="fontFamily = $event"
    >
    </FontPicker>

    <transition name="slide-fade">
      <div
        v-if="collapsed"
        class="collapsed-menu"
        :class="{ 'no-border': noBorder }"
      >
        <div class="mt-2 flex flex-col gap-2">
          <t-button-group flex :label="$t('forms.text_style')">
            <t-group-button
              small
              toggle
              :active="fontWeight === 'bold'"
              @button-clicked="fontWeight = $event"
            >
              <font-awesome-icon :icon="['fas', 'bold']" />
            </t-group-button>
            <t-group-button
              data="italic"
              small
              toggle
              :active="fontStyle === 'italic'"
              @button-clicked="fontStyle = $event"
            >
              <font-awesome-icon :icon="['fas', 'italic']" />
            </t-group-button>
            <t-group-button
              data="underline"
              small
              toggle
              :active="textDecoration === 'underline'"
              @button-clicked="textDecoration = $event"
            >
              <font-awesome-icon :icon="['fas', 'underline']" />
            </t-group-button>
            <t-group-button
              data="line-through"
              small
              toggle
              :active="textDecoration === 'line-through'"
              @button-clicked="textDecoration = $event"
            >
              <font-awesome-icon :icon="['fas', 'strikethrough']" />
            </t-group-button>
          </t-button-group>

          <t-button-group flex :label="$t('forms.text_transform')">
            <t-group-button
              data="capitalize"
              small
              :active="textTransform === 'capitalize'"
              @button-clicked="setTextTransform('capitalize')"
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <g clip-path="url(#clip0_1_59)">
                  <path
                    d="M3.3315 15H1.81729L5.21964 5.54545H6.86772L10.2701 15H8.75586L6.08292 7.26278H6.00906L3.3315 15ZM3.5854 11.2976H8.49734V12.4979H3.5854V11.2976ZM14.3833 17.8068C13.8201 17.8068 13.3354 17.733 12.9292 17.5852C12.526 17.4375 12.1967 17.2421 11.9412 16.9989C11.6858 16.7558 11.495 16.4896 11.3688 16.2003L12.5552 15.7109C12.6383 15.8464 12.7491 15.9895 12.8876 16.1403C13.0292 16.2942 13.22 16.425 13.46 16.5327C13.7032 16.6404 14.0156 16.6942 14.3972 16.6942C14.9204 16.6942 15.3528 16.5665 15.6944 16.3111C16.036 16.0587 16.2069 15.6555 16.2069 15.1016V13.7074H16.1191C16.036 13.8582 15.916 14.0259 15.7591 14.2106C15.6052 14.3952 15.3928 14.5553 15.122 14.6907C14.8511 14.8261 14.4988 14.8938 14.0648 14.8938C13.5047 14.8938 12.9999 14.763 12.5506 14.5014C12.1043 14.2367 11.7504 13.8474 11.4888 13.3335C11.2303 12.8164 11.101 12.1809 11.101 11.4268C11.101 10.6728 11.2288 10.0265 11.4842 9.48793C11.7427 8.94934 12.0967 8.53693 12.546 8.25071C12.9953 7.96141 13.5047 7.81676 14.074 7.81676C14.5141 7.81676 14.8696 7.89062 15.1404 8.03835C15.4113 8.183 15.6221 8.35227 15.7729 8.54616C15.9268 8.74006 16.0453 8.91087 16.1284 9.05859H16.2299V7.90909H17.5826V15.157C17.5826 15.7663 17.441 16.2665 17.1578 16.6573C16.8747 17.0482 16.4915 17.3375 16.0083 17.5252C15.5282 17.7129 14.9866 17.8068 14.3833 17.8068ZM14.3695 13.7489C14.7665 13.7489 15.102 13.6566 15.3759 13.4719C15.6529 13.2842 15.8622 13.0165 16.0037 12.6687C16.1484 12.3178 16.2207 11.8977 16.2207 11.4084C16.2207 10.9313 16.1499 10.5112 16.0083 10.1481C15.8668 9.78492 15.659 9.50178 15.3851 9.29865C15.1112 9.09245 14.7727 8.98935 14.3695 8.98935C13.954 8.98935 13.6078 9.09706 13.3308 9.3125C13.0538 9.52486 12.8445 9.81416 12.7029 10.1804C12.5645 10.5466 12.4952 10.956 12.4952 11.4084C12.4952 11.8731 12.566 12.2809 12.7076 12.6317C12.8491 12.9826 13.0584 13.2565 13.3354 13.4535C13.6155 13.6504 13.9602 13.7489 14.3695 13.7489Z"
                    fill="currentColor"
                  />
                </g>
                <defs>
                  <clipPath id="clip0_1_59">
                    <rect width="20" height="20" fill="white" />
                  </clipPath>
                </defs>
              </svg>
            </t-group-button>
            <t-group-button
              data="lowercase"
              small
              :active="textTransform === 'lowercase'"
              @button-clicked="setTextTransform('lowercase')"
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <g clip-path="url(#clip0_1_61)">
                  <path
                    d="M5.27907 15.157C4.82974 15.157 4.42349 15.0739 4.06032 14.9077C3.69716 14.7384 3.4094 14.4937 3.19704 14.1737C2.98776 13.8536 2.88312 13.4612 2.88312 12.9964C2.88312 12.5964 2.96006 12.267 3.11395 12.0085C3.26783 11.75 3.47557 11.5453 3.73717 11.3945C3.99877 11.2437 4.29115 11.1299 4.6143 11.0529C4.93746 10.976 5.26676 10.9175 5.60223 10.8775C6.02694 10.8282 6.37164 10.7882 6.63632 10.7575C6.901 10.7236 7.09335 10.6697 7.21338 10.5959C7.33341 10.522 7.39342 10.402 7.39342 10.2358V10.2035C7.39342 9.80031 7.27955 9.48793 7.0518 9.26633C6.82713 9.04474 6.49167 8.93395 6.04541 8.93395C5.58068 8.93395 5.21444 9.03705 4.94669 9.24325C4.68201 9.44638 4.49889 9.67259 4.39733 9.92188L3.1001 9.62642C3.25398 9.19555 3.47865 8.84777 3.7741 8.5831C4.07264 8.31534 4.41579 8.12145 4.80358 8.00142C5.19136 7.87831 5.59915 7.81676 6.02694 7.81676C6.31009 7.81676 6.61016 7.85062 6.92716 7.91832C7.24723 7.98295 7.54577 8.10298 7.82275 8.27841C8.10282 8.45383 8.33211 8.70466 8.51061 9.03089C8.68911 9.35405 8.77836 9.77415 8.77836 10.2912V15H7.43035V14.0305H7.37496C7.2857 14.209 7.15183 14.3845 6.97332 14.5568C6.79482 14.7292 6.56553 14.8723 6.28547 14.9862C6.0054 15.1 5.66994 15.157 5.27907 15.157ZM5.57915 14.049C5.96077 14.049 6.28701 13.9736 6.55784 13.8228C6.83175 13.672 7.03949 13.475 7.18106 13.2319C7.32571 12.9857 7.39804 12.7225 7.39804 12.4425V11.5284C7.3488 11.5777 7.25339 11.6238 7.11182 11.6669C6.97332 11.7069 6.81482 11.7423 6.63632 11.7731C6.45782 11.8008 6.28393 11.8269 6.11466 11.8516C5.94539 11.8731 5.80381 11.8916 5.68994 11.907C5.42219 11.9408 5.17751 11.9978 4.95592 12.0778C4.73741 12.1578 4.56198 12.2732 4.42964 12.424C4.30038 12.5717 4.23575 12.7687 4.23575 13.0149C4.23575 13.3565 4.36193 13.6151 4.6143 13.7905C4.86667 13.9628 5.18828 14.049 5.57915 14.049ZM13.5899 17.8068C13.0267 17.8068 12.5419 17.733 12.1357 17.5852C11.7325 17.4375 11.4032 17.2421 11.1478 16.9989C10.8923 16.7558 10.7015 16.4896 10.5753 16.2003L11.7618 15.7109C11.8449 15.8464 11.9557 15.9895 12.0941 16.1403C12.2357 16.2942 12.4265 16.425 12.6666 16.5327C12.9097 16.6404 13.2221 16.6942 13.6037 16.6942C14.1269 16.6942 14.5593 16.5665 14.901 16.3111C15.2426 16.0587 15.4134 15.6555 15.4134 15.1016V13.7074H15.3257C15.2426 13.8582 15.1226 14.0259 14.9656 14.2106C14.8117 14.3952 14.5994 14.5553 14.3285 14.6907C14.0577 14.8261 13.7053 14.8938 13.2714 14.8938C12.7112 14.8938 12.2065 14.763 11.7571 14.5014C11.3109 14.2367 10.957 13.8474 10.6954 13.3335C10.4368 12.8164 10.3076 12.1809 10.3076 11.4268C10.3076 10.6728 10.4353 10.0265 10.6907 9.48793C10.9493 8.94934 11.3032 8.53693 11.7525 8.25071C12.2019 7.96141 12.7112 7.81676 13.2806 7.81676C13.7207 7.81676 14.0762 7.89062 14.347 8.03835C14.6178 8.183 14.8286 8.35227 14.9794 8.54616C15.1333 8.74006 15.2518 8.91087 15.3349 9.05859H15.4365V7.90909H16.7891V15.157C16.7891 15.7663 16.6475 16.2665 16.3644 16.6573C16.0812 17.0482 15.6981 17.3375 15.2149 17.5252C14.7348 17.7129 14.1931 17.8068 13.5899 17.8068ZM13.576 13.7489C13.9731 13.7489 14.3085 13.6566 14.5824 13.4719C14.8594 13.2842 15.0687 13.0165 15.2103 12.6687C15.3549 12.3178 15.4272 11.8977 15.4272 11.4084C15.4272 10.9313 15.3565 10.5112 15.2149 10.1481C15.0733 9.78492 14.8656 9.50178 14.5917 9.29865C14.3178 9.09245 13.9792 8.98935 13.576 8.98935C13.1606 8.98935 12.8143 9.09706 12.5373 9.3125C12.2603 9.52486 12.0511 9.81416 11.9095 10.1804C11.771 10.5466 11.7017 10.956 11.7017 11.4084C11.7017 11.8731 11.7725 12.2809 11.9141 12.6317C12.0557 12.9826 12.265 13.2565 12.5419 13.4535C12.822 13.6504 13.1667 13.7489 13.576 13.7489Z"
                    fill="currentColor"
                  />
                </g>
                <defs>
                  <clipPath id="clip0_1_61">
                    <rect width="20" height="20" fill="white" />
                  </clipPath>
                </defs>
              </svg>
            </t-group-button>
            <t-group-button
              data="uppercase"
              small
              :active="textTransform === 'uppercase'"
              @button-clicked="setTextTransform('uppercase')"
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <g clip-path="url(#clip0_1_63)">
                  <path
                    d="M2.6396 15H1.1254L4.52774 5.54545H6.17583L9.57817 15H8.06396L5.39102 7.26278H5.31716L2.6396 15ZM2.89351 11.2976H7.80544V12.4979H2.89351V11.2976ZM16.8976 8.53232C16.8083 8.25225 16.6883 8.00142 16.5375 7.77983C16.3898 7.55516 16.2128 7.36435 16.0066 7.20739C15.8004 7.04735 15.565 6.92578 15.3003 6.84268C15.0387 6.75959 14.7509 6.71804 14.437 6.71804C13.9046 6.71804 13.4245 6.855 12.9967 7.12891C12.5689 7.40282 12.2303 7.80445 11.981 8.33381C11.7348 8.86008 11.6117 9.50485 11.6117 10.2681C11.6117 11.0344 11.7364 11.6823 11.9857 12.2116C12.235 12.741 12.5766 13.1426 13.0105 13.4165C13.4445 13.6905 13.9384 13.8274 14.4924 13.8274C15.0064 13.8274 15.4542 13.7228 15.8358 13.5135C16.2205 13.3042 16.5175 13.0088 16.7268 12.6271C16.9391 12.2424 17.0453 11.79 17.0453 11.2699L17.4146 11.3391H14.7094V10.1619H18.4256V11.2376C18.4256 12.0316 18.2564 12.721 17.9178 13.3058C17.5824 13.8874 17.1176 14.3368 16.5237 14.6538C15.9328 14.9708 15.2557 15.1293 14.4924 15.1293C13.6368 15.1293 12.8859 14.9323 12.2396 14.5384C11.5963 14.1444 11.0947 13.5858 10.7346 12.8626C10.3745 12.1362 10.1945 11.2745 10.1945 10.2773C10.1945 9.52332 10.2991 8.84624 10.5084 8.24609C10.7177 7.64595 11.0116 7.1366 11.3901 6.71804C11.7718 6.2964 12.2196 5.97479 12.7335 5.7532C13.2506 5.52853 13.8153 5.41619 14.4278 5.41619C14.9387 5.41619 15.4142 5.4916 15.8543 5.6424C16.2975 5.79321 16.6914 6.0071 17.0361 6.28409C17.3839 6.56108 17.6716 6.89039 17.8994 7.27202C18.1271 7.65057 18.281 8.07067 18.361 8.53232H16.8976Z"
                    fill="currentColor"
                  />
                </g>
                <defs>
                  <clipPath id="clip0_1_63">
                    <rect width="20" height="20" fill="white" />
                  </clipPath>
                </defs>
              </svg>
            </t-group-button>
          </t-button-group>
        </div>
        <content-row class="!block" :margin-bottom="0" :margin-top="8">
          <t-input-number
            v-if="blockName !== 'button'"
            v-model="letterSpacing"
            flex
            :min="0"
            unit=""
            :label="$t('forms.letter_spacing')"
          ></t-input-number>
        </content-row>
        <content-row class="!block" :margin-bottom="0" :margin-top="8">
          <t-input-number
            v-if="blockName !== 'button'"
            v-model="lineHeight"
            flex
            data-testid="line-height-precise"
            :label="$t('forms.line_height')"
            :min="1.0"
            :max="4.0"
            unit=""
            step="0.01"
            regex-pattern="[0,9]{3,4}"
          ></t-input-number>
        </content-row>
      </div>
    </transition>
  </div>
</template>

<script lang="ts" setup>
  import convertLegacyHtmlString from "../../utils/covertFontFamilyToInherit";
  import { useFontStore } from "@/stores/fonts";
  import { useMjmlStore } from "@/stores/mjmlStore";
  import { useColorsStore } from "@/stores/colors";
  import { useGeneralStore } from "@/stores/generalStore";
  import IFont from "types/IFont/IFont";

  import { TButtonGroup, TGroupButton, TInputNumber } from "@topol/twig-ui";
  import FontPicker from "@/forms/components/FontSizeColor/FontPicker.vue";

  import { useI18n } from "vue-i18n";
  import { computed, ref } from "vue";

  const props = defineProps({
    blockName: {
      type: String,
      required: true,
    },

    prodstructure: {
      type: [Object, Boolean],
      required: true,
    },

    noBorder: {
      type: Boolean,
      default: false,
    },
  });

  const fontStore = useFontStore();
  const mjmlStore = useMjmlStore();
  const colorsStore = useColorsStore();
  const generalStore = useGeneralStore();
  const { t } = useI18n();

  const collapsed = ref(false);
  const templateColors = ref<string[]>([]);

  const fonts = computed(() => {
    const defaultFont: IFont = {
      style: "",
      label: t("forms.none"),
    };

    return [defaultFont, ...fontStore.fonts];
  });

  const sizes = computed(() => {
    return fontStore.fontSizes;
  });

  const fontWeight = computed({
    get() {
      return (
        mjmlStore.getActiveBlockAttrString(props.blockName + "-font-weight") ||
        "normal"
      );
    },
    set() {
      let val = "bold";

      if (fontWeight.value === "bold") {
        val = "normal";
      }

      mjmlStore.updateBlockAttribute({
        prop: `${props.blockName}-font-weight`,
        value: val,
        product: true,
        prodstructure: props.prodstructure,
      });
    },
  });

  const fontStyle = computed({
    get() {
      return (
        mjmlStore.getActiveBlockAttrString(props.blockName + "-font-style") ||
        "normal"
      );
    },
    set(val: string) {
      if (val !== null || val !== undefined) {
        mjmlStore.updateBlockAttribute({
          prop: `${props.blockName}-font-style`,
          value: val,
          product: true,
          prodstructure: props.prodstructure,
        });
      }
    },
  });

  const letterSpacing = computed({
    get() {
      return (
        parseInt(
          mjmlStore.getActiveBlockAttrString(
            props.blockName + "-letter-spacing"
          )
        ) || 0
      );
    },
    set(val: number) {
      if (val !== null || val !== undefined) {
        mjmlStore.updateBlockAttribute({
          prop: `${props.blockName}-letter-spacing`,
          value: val + "px",
          product: true,
          prodstructure: props.prodstructure,
        });
      }
    },
  });

  const textTransform = computed({
    get() {
      return (
        mjmlStore.getActiveBlockAttrString(
          props.blockName + "-text-transform"
        ) || null
      );
    },
    set(val: string) {
      if (val !== null || val !== undefined) {
        mjmlStore.updateBlockAttribute({
          prop: `${props.blockName}-text-transform`,
          value: val,
          product: true,
          prodstructure: props.prodstructure,
        });
      }
    },
  });

  const textDecoration = computed({
    get() {
      return (
        mjmlStore.getActiveBlockAttrString(
          props.blockName + "-text-decoration"
        ) || null
      );
    },
    set(val: string) {
      if (val !== null || val !== undefined) {
        mjmlStore.updateBlockAttribute({
          prop: `${props.blockName}-text-decoration`,
          value: val,
          product: true,
          prodstructure: props.prodstructure,
        });
      }
    },
  });

  const color = computed({
    get() {
      return (
        mjmlStore.getActiveBlockAttrString(props.blockName + "-color") ||
        "#000000"
      );
    },
    set(val: string) {
      mjmlStore.updateBlockAttribute({
        prop: `${props.blockName}-color`,
        value: val,
        product: true,
        prodstructure: props.prodstructure,
      });
    },
  });

  const size = computed({
    get() {
      return (
        parseInt(
          mjmlStore.getActiveBlockAttrString(props.blockName + "-font-size")
        ) || 11
      );
    },
    set(val: number) {
      if (val !== undefined) {
        mjmlStore.updateBlockAttribute({
          prop: `${props.blockName}-font-size`,
          value: val + "px",
          product: true,
          prodstructure: props.prodstructure,
        });
      }
    },
  });

  const lineHeight = computed({
    get() {
      return Number(
        mjmlStore.getActiveBlockAttrString(props.blockName + "-line-height") ||
          "1"
      );
    },
    set(val: number) {
      if (val !== null || val !== undefined) {
        val = Math.round(val * 100) / 100;
        mjmlStore.updateBlockAttribute({
          prop: `${props.blockName}-line-height`,
          value: val,
          product: true,
          prodstructure: props.prodstructure,
        });
      }
    },
  });

  const fontFamily = computed({
    get() {
      return (
        mjmlStore.getActiveBlockAttrString(props.blockName + "-font-family") ||
        undefined
      );
    },
    set(val: IFont) {
      //clean legacy html structure
      const originalHTMLStringContent = mjmlStore.getActiveBlockAttrString(
        props.blockName
      );
      const convertedString = convertLegacyHtmlString(
        originalHTMLStringContent || ""
      );

      mjmlStore.updateBlockAttribute({
        prop: `${props.blockName}`,
        value: convertedString,
        product: true,
        prodstructure: props.prodstructure,
      });

      if (val === undefined) {
        mjmlStore.updateBlockAttribute({
          prop: `${props.blockName}-font-family`,
          value: undefined,
          product: true,
          prodstructure: props.prodstructure,
        });
      }

      if (val !== undefined) {
        mjmlStore.updateBlockAttribute({
          prop: `${props.blockName}-font-family`,
          value: val.style,
          product: true,
          prodstructure: props.prodstructure,
        });
      }
    },
  });

  const colors = computed(() => colorsStore.colors);

  function getAllColorsFromTemplate() {
    const templateString = JSON.stringify(mjmlStore.getMjml()).toLowerCase();
    const hexColorPattern = /#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})/g;
    const colors = templateString.match(hexColorPattern);
    // templateColors.value = [...new Set(colors)];
  }

  function setTextTransform(val: string) {
    if (textTransform.value !== val) {
      textTransform.value = val;
    } else {
      textTransform.value = "none";
    }
  }
</script>

<style lang="scss">
  // @import "../components/FontSizeColor/FontSizeColor.scss";
</style>

<style scoped>
  .slide-fade-enter-active {
    transition: all 0.2s ease-in;
  }

  .slide-fade-enter,
  .slide-fade-leave-to {
    transform: translateY(-5px);
    opacity: 0;
  }
</style>
